import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add,
  Delete,
  Store as StoreIcon,
  EmojiEvents,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { storeDrawService, StoreDraw } from '../services/storeDrawService';
import { useToast } from '../contexts/ToastContext';

const StoreDrawList: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [storeDraws, setStoreDraws] = useState<StoreDraw[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDraw, setSelectedDraw] = useState<StoreDraw | null>(null);

  useEffect(() => {
    fetchStoreDraws();
  }, []);

  const fetchStoreDraws = async () => {
    try {
      setLoading(true);
      const data = await storeDrawService.getAllStoreDraws();
      setStoreDraws(data);
    } catch (error) {
      console.error('Erro ao carregar sorteios de lojas:', error);
      showError('Erro ao carregar sorteios de lojas');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (draw: StoreDraw) => {
    setSelectedDraw(draw);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDraw) return;

    try {
      await storeDrawService.deleteStoreDraw(selectedDraw.id);
      showSuccess('Sorteio de loja removido com sucesso!');
      setDeleteDialogOpen(false);
      setSelectedDraw(null);
      fetchStoreDraws();
    } catch (error) {
      console.error('Erro ao deletar sorteio:', error);
      showError('Erro ao deletar sorteio de loja');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <>
      <AppHeader
        title="Sorteios de Lojas"
        subtitle="Histórico de sorteios realizados para lojas participantes"
        icon={<StoreIcon />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Histórico de Sorteios
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/store-draws/new')}
            sx={{
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            Realizar Sorteio ao Vivo
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : storeDraws.length === 0 ? (
          <Paper
            elevation={2}
            sx={{
              p: 6,
              textAlign: 'center',
              borderRadius: 2,
              backgroundColor: 'grey.50',
            }}
          >
            <EmojiEvents sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum sorteio de loja realizado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Comece sorteando uma loja participante da campanha
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/store-draws/new')}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Realizar Sorteio ao Vivo
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                    Campanha
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                    Prêmio
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                    Loja Vencedora
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }}>
                    Data do Sorteio
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, color: 'white' }}
                  >
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {storeDraws.map((draw) => (
                  <TableRow
                    key={draw.id}
                    hover
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {draw.campaign.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={draw.prizeDescription}
                        color="secondary"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          🏆 {draw.winnerStore.name}
                        </Typography>
                        {draw.winnerStore.address && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {draw.winnerStore.address}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(draw.drawDate)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(draw)}
                        title="Deletar sorteio"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Tem certeza que deseja excluir este sorteio de loja?
          </Alert>
          {selectedDraw && (
            <Box>
              <Typography variant="body2">
                <strong>Campanha:</strong> {selectedDraw.campaign.name}
              </Typography>
              <Typography variant="body2">
                <strong>Prêmio:</strong> {selectedDraw.prizeDescription}
              </Typography>
              <Typography variant="body2">
                <strong>Loja Vencedora:</strong> {selectedDraw.winnerStore.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Confirmar Exclusão
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StoreDrawList;

